/**
 * lh-cart.js — Lieblingshaus native Shopify AJAX cart
 * Replaces Webflow/Udesly w-commerce cart logic.
 *
 * Features:
 *  - Open/close slide-in sidebar
 *  - Render cart items from /cart.js
 *  - Add-to-cart via /cart/add.js (intercepts product forms)
 *  - Update quantity via /cart/change.js
 *  - Remove item via /cart/change.js (qty → 0)
 *  - Cart count badge (all .lh-cart-count elements)
 *  - Subtotal display
 *  - Keyboard (Esc) + backdrop-click to close
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────────────────────────
   * Money formatting
   * Read money_format from <html data-money-format="…">
   * ───────────────────────────────────────────────────────────────────────────── */
  var moneyFormat = document.documentElement.dataset.moneyFormat || '{{amount_with_comma_separator}} &euro;';

  function formatMoney(cents) {
    var value = Math.round(Number(cents));
    var amount;

    if (moneyFormat.indexOf('{{amount_with_comma_separator}}') !== -1 ||
        moneyFormat.indexOf('{{ amount_with_comma_separator }}') !== -1) {
      amount = (value / 100).toFixed(2).replace('.', ',');
      return moneyFormat.replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount);
    }

    if (moneyFormat.indexOf('{{amount_no_decimals_with_comma_separator}}') !== -1 ||
        moneyFormat.indexOf('{{ amount_no_decimals_with_comma_separator }}') !== -1) {
      amount = Math.round(value / 100).toString();
      return moneyFormat.replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, amount);
    }

    if (moneyFormat.indexOf('{{amount_no_decimals}}') !== -1 ||
        moneyFormat.indexOf('{{ amount_no_decimals }}') !== -1) {
      amount = Math.round(value / 100).toString();
      return moneyFormat.replace(/\{\{\s*amount_no_decimals\s*\}\}/, amount);
    }

    // Default: {{amount}}
    amount = (value / 100).toFixed(2);
    return moneyFormat.replace(/\{\{\s*amount\s*\}\}/, amount);
  }

  /* ─────────────────────────────────────────────────────────────────────────────
   * DOM helpers
   * ───────────────────────────────────────────────────────────────────────────── */
  function qs(sel) { return document.querySelector(sel); }
  function qsa(sel) { return document.querySelectorAll(sel); }

  /* ─────────────────────────────────────────────────────────────────────────────
   * Open / Close
   * ───────────────────────────────────────────────────────────────────────────── */
  var drawer = qs('#lh-cart-drawer');

  function openCart() {
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.removeAttribute('aria-hidden');
    document.body.classList.add('lh-cart-open');
    loadCart();
  }

  function closeCart() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('lh-cart-open');
  }

  // Close on backdrop click (click directly on drawer overlay, not panel)
  if (drawer) {
    drawer.addEventListener('click', function (e) {
      if (e.target === drawer) closeCart();
    });
  }

  // Close button
  document.addEventListener('click', function (e) {
    if (e.target.closest('#lh-cart-close')) { e.preventDefault(); closeCart(); }
  });

  // Open buttons (data-lh-cart-open or .lh-cart-open)
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-lh-cart-open]')) { e.preventDefault(); openCart(); }
  });

  // Esc key
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeCart();
  });

  /* ─────────────────────────────────────────────────────────────────────────────
   * Fetch cart & render
   * ───────────────────────────────────────────────────────────────────────────── */
  function loadCart() {
    fetch('/cart.js', {
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(renderCart)
      .catch(function (err) {
        console.error('[lh-cart] fetch error', err);
        showCartError('Fehler beim Laden des Warenkorbs.');
      });
  }

  function renderCart(cart) {
    updateBadge(cart.item_count);

    var listEl       = qs('#lh-cart-list');
    var itemsWrap    = qs('#lh-cart-items-wrap');
    var emptyEl      = qs('#lh-cart-empty');
    var subtotalEl   = qs('#lh-cart-subtotal');
    var errorEl      = qs('#lh-cart-error');

    if (errorEl) errorEl.style.display = 'none';

    if (cart.item_count === 0) {
      if (itemsWrap) itemsWrap.style.display = 'none';
      if (emptyEl)   emptyEl.style.display   = '';
    } else {
      if (emptyEl)   emptyEl.style.display   = 'none';
      if (itemsWrap) itemsWrap.style.display = '';

      if (subtotalEl) {
        subtotalEl.textContent = formatMoney(cart.items_subtotal_price);
      }

      if (listEl) {
        listEl.innerHTML = cart.items.map(buildItemHtml).join('');
      }

      updateRecommendations(cart.items);
    }
  }

  /* ─────────────────────────────────────────────────────────────────────────────
   * Dynamic cart recommendations ("Passend dazu")
   *
   * Detection is tag-based (fetches /products/{handle}.js for each unique
   * product in the cart to read tags — parallel fetches, browser-cached).
   *
   * Expected tags on paint products:
   *   wandfarbe   — wall paint
   *   moebelfarbe — furniture paint
   *
   * Scenarios (matching data-rec-scenarios in cart-drawer.liquid):
   *   wandfarbe-ptp   — wall paint + Painting the Past
   *   wandfarbe-pow   — wall paint + Paint of Walinoon
   *   mobelfarbe-ptp  — furniture paint + Painting the Past
   *   mobelfarbe-pow  — furniture paint + Paint of Walinoon
   *   default         — always visible (static fallback)
   * ───────────────────────────────────────────────────────────────────────────── */
  function detectScenario(items, tagMap) {
    var hasWandfarbe  = false;
    var hasMobelfarbe = false;
    var brand         = null;

    items.forEach(function (item) {
      var tags   = tagMap[item.handle] || [];
      var vendor = (item.vendor || '').toLowerCase();

      var isWand  = tags.indexOf('wandfarbe')   !== -1;
      var isMobel = tags.indexOf('moebelfarbe') !== -1;

      if (isWand) {
        hasWandfarbe = true;
        if (!brand) brand = vendor;
      } else if (isMobel) {
        hasMobelfarbe = true;
        if (!brand) brand = vendor;
      }
    });

    console.log('[lh-cart] detectScenario — wand:', hasWandfarbe, 'mobel:', hasMobelfarbe, 'brand:', brand);

    if (!hasWandfarbe && !hasMobelfarbe) return 'default';

    var isPtp = brand && brand.indexOf('painting the past') !== -1;
    var isPow = brand && (brand.indexOf('walinoon') !== -1 || brand.indexOf('paint of walinoon') !== -1);

    if (hasWandfarbe) {
      if (isPtp) return 'wandfarbe-ptp';
      if (isPow) return 'wandfarbe-pow';
      return 'default';
    }

    // hasMobelfarbe
    if (isPtp) return 'mobelfarbe-ptp';
    if (isPow) return 'mobelfarbe-pow';
    return 'default';
  }

  function applyScenario(scenario, slider) {
    console.log('[lh-cart] rec scenario:', scenario);
    slider.querySelectorAll('[data-rec-scenarios]').forEach(function (el) {
      var sc = el.getAttribute('data-rec-scenarios').split(' ');
      // Always show 'default' static items; also show items matching the active scenario
      el.style.display = (sc.indexOf('default') !== -1 || sc.indexOf(scenario) !== -1) ? '' : 'none';
    });
  }

  function updateRecommendations(items) {
    var slider = qs('#lh-rec-slider');
    if (!slider) return;

    // Collect unique product handles from cart
    var handles = [];
    items.forEach(function (item) {
      if (item.handle && handles.indexOf(item.handle) === -1) handles.push(item.handle);
    });

    if (!handles.length) {
      applyScenario('default', slider);
      return;
    }

    // Fetch /products/{handle}.js in parallel to read tags
    Promise.all(handles.map(function (h) {
      return fetch('/products/' + h + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .catch(function () { return { handle: h, tags: [] }; });
    })).then(function (products) {
      var tagMap = {};
      products.forEach(function (p) { tagMap[p.handle] = p.tags || []; });
      applyScenario(detectScenario(items, tagMap), slider);
    }).catch(function () {
      applyScenario('default', slider);
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────────
   * Item HTML builder
   * ───────────────────────────────────────────────────────────────────────────── */
  function buildItemHtml(item) {
    var imageHtml = item.image
      ? '<a href="' + esc(item.url) + '" class="cart-modal_image-link" tabindex="-1" aria-hidden="true">' +
          '<img class="cart-modal_image" src="' + esc(item.image) + '" alt="' + esc(item.title) + '" loading="lazy">' +
        '</a>'
      : '<div class="cart-modal_image-link"></div>';

    // Variant options: show "Menge: 2,5L" (name + value, black text)
    var variantLine = '';
    if (item.options_with_values && item.options_with_values.length > 0) {
      item.options_with_values.forEach(function (opt) {
        if (opt.value && opt.value !== 'Default Title') {
          variantLine += '<p class="text-size-small margin-0">' +
            escHtml(opt.name) + ': ' + escHtml(opt.value) +
          '</p>';
        }
      });
    } else if (item.variant_title && item.variant_title !== 'Default Title') {
      // Fallback if options_with_values is missing
      variantLine = '<p class="text-size-small margin-0">' + escHtml(item.variant_title) + '</p>';
    }

    var propsHtml = '';
    if (item.properties) {
      Object.keys(item.properties).forEach(function (k) {
        if (k.charAt(0) !== '_') {
          propsHtml += '<p class="text-size-small margin-0 text-color-grey">' +
            escHtml(k) + ': ' + escHtml(String(item.properties[k])) +
            '</p>';
        }
      });
    }

    return [
      '<div class="cart-modal_item w-commerce-commercecartitem" data-item-key="' + esc(item.key) + '">',
        imageHtml,
        '<div class="cart-modal_item-content-wrapper">',
          '<div class="cart-modal_content w-commerce-commercecartiteminfo">',
            '<a href="' + esc(item.url) + '" class="cart-modal_item-link">',
              '<div class="heading-style-h6 w-commerce-commercecartproductname">' + escHtml(item.product_title) + '</div>',
            '</a>',
            '<div class="text-weight-normal">' + formatMoney(item.line_price) + '</div>',
            variantLine,
            propsHtml,
            '<button type="button"',
              ' class="lh-cart-remove text-color-grey text-size-tiny"',
              ' data-item-key="' + esc(item.key) + '"',
              ' aria-label="' + esc(item.product_title) + ' entfernen">',
              'Löschen',
            '</button>',
          '</div>',
          '<input',
            ' type="number"',
            ' class="w-commerce-commercecartquantity quantiy-select lh-qty-input"',
            ' value="' + item.quantity + '"',
            ' min="1"',
            ' aria-label="Anzahl"',
            ' data-item-key="' + esc(item.key) + '">',
        '</div>',
      '</div>'
    ].join('');
  }

  /* ─────────────────────────────────────────────────────────────────────────────
   * Badge update (multiple .lh-cart-count elements — desktop + mobile buttons)
   * ───────────────────────────────────────────────────────────────────────────── */
  function updateBadge(count) {
    qsa('.lh-cart-count').forEach(function (el) {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────────
   * Add to cart — intercept product form submit
   *
   * Use capture phase so this fires BEFORE lieblingshaus.js bubble-phase listeners.
   * stopPropagation() prevents the Webflow/Udesly bundle from also intercepting.
   * ───────────────────────────────────────────────────────────────────────────── */
  // capture: true → fires BEFORE lieblingshaus.js bubble-phase listeners on the form
  document.addEventListener('submit', function (e) {
    var form = e.target;
    // Match Shopify product forms (action="/cart/add") and Udesly-attributed forms
    if (!form.matches('form[action="/cart/add"], form[data-node-type="commerce-add-to-cart-form"]')) return;
    e.preventDefault();
    e.stopPropagation(); // prevent lieblingshaus.js from double-handling

    var btn = form.querySelector('[data-node-type="commerce-add-to-cart-button"], [type="submit"]');
    var origBtnText;

    if (btn) {
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      origBtnText = btn.tagName === 'INPUT' ? btn.value : btn.textContent;
      var loadingText = btn.dataset.loadingText || 'Gute Wahl!';
      if (btn.tagName === 'INPUT') btn.value = loadingText;
      else btn.textContent = loadingText;
    }

    // Hide previous errors
    var errWrap = form.querySelector('.w-commerce-commerceaddtocarterror, .error-state');
    if (errWrap) errWrap.style.display = 'none';

    var formData = new FormData(form);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
      body: formData
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (d) { throw d; });
        return r.json();
      })
      .then(function () {
        openCart();
      })
      .catch(function (errData) {
        var msg = (errData && errData.description)
          ? errData.description
          : 'Beim Hinzufügen ist ein Fehler aufgetreten.';
        if (errWrap) {
          errWrap.textContent = msg;
          errWrap.style.display = '';
        }
      })
      .finally(function () {
        if (btn) {
          btn.disabled = false;
          btn.setAttribute('aria-busy', 'false');
          if (btn.tagName === 'INPUT') btn.value = origBtnText;
          else btn.textContent = origBtnText;
        }
      });
  }, true); // capture phase — runs before lieblingshaus.js bubble listeners

  /* ─────────────────────────────────────────────────────────────────────────────
   * Quantity change (live — on change of .lh-qty-input inside drawer)
   * ───────────────────────────────────────────────────────────────────────────── */
  document.addEventListener('change', function (e) {
    if (!e.target.classList.contains('lh-qty-input')) return;
    var key = e.target.dataset.itemKey;
    var qty = parseInt(e.target.value, 10);
    if (!key || isNaN(qty) || qty < 0) return;
    changeItem(key, qty);
  });

  /* ─────────────────────────────────────────────────────────────────────────────
   * Remove item
   * ───────────────────────────────────────────────────────────────────────────── */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.lh-cart-remove');
    if (!btn) return;
    e.preventDefault();
    var key = btn.dataset.itemKey;
    if (key) changeItem(key, 0);
  });

  function changeItem(key, quantity) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(renderCart)
      .catch(function (err) {
        console.error('[lh-cart] change error', err);
      });
  }

  /* ─────────────────────────────────────────────────────────────────────────────
   * Error display
   * ───────────────────────────────────────────────────────────────────────────── */
  function showCartError(msg) {
    var errorEl  = qs('#lh-cart-error');
    var errorMsg = qs('#lh-cart-error-msg');
    if (errorEl)  errorEl.style.display = '';
    if (errorMsg) errorMsg.textContent  = msg;
  }

  /* ─────────────────────────────────────────────────────────────────────────────
   * Escape helpers
   * ───────────────────────────────────────────────────────────────────────────── */
  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
  function esc(str) { return escHtml(str); }

  /* ─────────────────────────────────────────────────────────────────────────────
   * Init: fetch real cart count from API on every page load.
   *
   * The original Udesly cart ALWAYS rendered the badge as display:none and
   * relied on JS to show it. We fetch /cart.js so the badge is always in sync,
   * even after a page reload mid-session.
   * ───────────────────────────────────────────────────────────────────────────── */
  (function initBadge() {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { updateBadge(cart.item_count); })
      .catch(function () {
        // Fallback: read from Liquid-rendered span if fetch fails
        var first = qs('.lh-cart-count');
        if (!first) return;
        var count = parseInt(first.textContent.trim(), 10) || 0;
        updateBadge(count);
      });
  })();

})();
